import { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// legacy-сборка worker нужна для Chrome 109 и Firefox 115 ESR
pdfjs.GlobalWorkerOptions.workerSrc =
  'https://cdn.jsdelivr.net/npm/pdfjs-dist@' +
  pdfjs.version +
  '/legacy/build/pdf.worker.min.mjs';

export const ASSET_VIEWER_MODAL_ID = 'taskAssetViewerModal';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

function getFileExtension(fileUrl) {
  const path = fileUrl.split(/[?#]/)[0];
  const fileName = path.substring(path.lastIndexOf('/') + 1);
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0) {
    return '';
  }
  return fileName.substring(lastDotIndex + 1).toLowerCase();
}

export function getAssetKind(asset) {
  if (asset.type !== 'FILE' || !asset.file) {
    return 'other';
  }
  const extension = getFileExtension(asset.file);
  if (IMAGE_EXTENSIONS.includes(extension)) {
    return 'image';
  }
  if (extension === 'pdf') {
    return 'pdf';
  }
  return 'other';
}

function getDownloadFileName(asset) {
  const extension = getFileExtension(asset.file);
  if (!extension || asset.name.toLowerCase().endsWith('.' + extension)) {
    return asset.name;
  }
  return asset.name + '.' + extension;
}

function PdfPageButtons({ numPages, pageNumber, previousPage, nextPage }) {
  if (!(numPages > 1)) {
    return null;
  }

  const leftDisabled = pageNumber <= 1;
  const rightDisabled = pageNumber >= numPages;

  return (
    <div className="btn-toolbar justify-content-between">
      <div className="mx-auto btn-group">
        <button
          type="button"
          className={(leftDisabled ? 'lk-svg-button-disabled' : 'lk-svg-button') + ' mx-2'}
          disabled={leftDisabled}
          onClick={previousPage}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="36" fill="currentColor" className="bi bi-caret-left-fill" viewBox="0 0 16 16">
            <path d="m3.86 8.753 5.482 4.796c.646.566 1.658.106 1.658-.753V3.204a1 1 0 0 0-1.659-.753l-5.48 4.796a1 1 0 0 0 0 1.506z" />
          </svg>
        </button>
        <div className="my-auto">
          Страница {pageNumber || (numPages ? 1 : '--')} из {numPages || '--'}
        </div>
        <button
          type="button"
          className={(rightDisabled ? 'lk-svg-button-disabled' : 'lk-svg-button') + ' mx-2'}
          disabled={rightDisabled}
          onClick={nextPage}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="36" fill="currentColor" className="bi bi-caret-right-fill" viewBox="0 0 16 16">
            <path d="m12.14 8.753-5.482 4.796c-.646.566-1.658.106-1.658-.753V3.204a1 1 0 0 1 1.659-.753l5.48 4.796a1 1 0 0 1 0 1.506z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function PdfViewer({ file }) {
  const [numPages, setNumPages] = useState();
  const [pageNumber, setPageNumber] = useState(1);

  function onDocumentLoadSuccess({ numPages: loadedNumPages }) {
    setNumPages(loadedNumPages);
    setPageNumber(1);
  }

  return (
    <>
      <Document
        file={file}
        onLoadSuccess={onDocumentLoadSuccess}
        loading="Загрузка документа..."
        error="Не удалось загрузить документ"
      >
        <Page
          pageNumber={pageNumber}
          renderAnnotationLayer={false}
          renderTextLayer={false}
          width={Math.min(window.screen.width * 0.56, 1200)}
        />
      </Document>
      <PdfPageButtons
        numPages={numPages}
        pageNumber={pageNumber}
        previousPage={() => setPageNumber((prev) => prev - 1)}
        nextPage={() => setPageNumber((prev) => prev + 1)}
      />
    </>
  );
}

const PRINT_IFRAME_ID = 'taskAssetPrintFrame';

// PDF печатается из blob: URL, т.к. /media может отдаваться с X-Frame-Options: DENY
async function printPdf(fileUrl) {
  const previousIframe = document.getElementById(PRINT_IFRAME_ID);
  if (previousIframe) {
    URL.revokeObjectURL(previousIframe.src);
    previousIframe.remove();
  }

  let blob;
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error('HTTP ' + response.status);
    }
    blob = await response.blob();
  } catch (error) {
    console.log(error);
    alert('Не удалось загрузить документ для печати');
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.id = PRINT_IFRAME_ID;
  iframe.style.display = 'none';
  iframe.src = URL.createObjectURL(blob);
  document.body.appendChild(iframe);

  iframe.onload = () => {
    setTimeout(() => {
      iframe.focus();
      iframe.contentWindow.print();
    });
  };
}

export default function AssetViewerModal({ asset }) {
  const kind = asset ? getAssetKind(asset) : 'other';

  return (
    <div
      className="modal fade modal-xl"
      id={ASSET_VIEWER_MODAL_ID}
      tabIndex="-1"
      aria-labelledby={ASSET_VIEWER_MODAL_ID + 'Label'}
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-scrollable modal-xl">
        <div className="modal-content">
          <div className="modal-header bg-light">
            <h2 className="modal-title fs-5" id={ASSET_VIEWER_MODAL_ID + 'Label'}>
              {asset?.name}
            </h2>
            <button type="button" className="btn-close me-3" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div className="modal-body">
            {kind === 'image' && (
              <div className="text-center">
                <img
                  src={asset.file}
                  alt={asset.name}
                  className="img-fluid"
                  style={{ maxHeight: '70vh' }}
                />
              </div>
            )}
            {kind === 'pdf' && <PdfViewer key={asset.id} file={asset.file} />}
          </div>
          <div className="modal-footer">
            {kind === 'pdf' && (
              <button type="button" className="btn lk-button" onClick={() => printPdf(asset.file)}>
                Печать
              </button>
            )}
            {kind !== 'other' && (
              <a className="mx-2 btn lk-button" href={asset.file} download={getDownloadFileName(asset)}>
                Скачать
              </a>
            )}
            <button type="button" className="btn btn-secondary lk-bootstrap-button" data-bs-dismiss="modal">
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
